import { useState } from "react";
import {
  Typography,
  DenovoIcon,
  Button,
  ToastQueue,
} from "@repo/design-system";

const CONTACT_EMAIL = "bsad@denovosciences.ai";

export function ContactPage() {
  const [copied, setCopied] = useState(false);

  const handleCopyEmail = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      ToastQueue.success("Email copied to clipboard");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      ToastQueue.danger("Couldn't copy email. Please copy it manually.");
    }
  };

  return (
    <div className="flex min-h-[calc(100vh-6rem)] flex-col items-center justify-center gap-8 bg-backgroundPrimary px-4 py-8 md:px-8 md:py-12">
      {/* Email icon + headline */}
      <div className="flex flex-col items-center gap-4">
        <div className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-borderBrand bg-backgroundBrand/10 shadow-brand">
          <DenovoIcon
            icon="email"
            width={40}
            height={40}
            className="text-contentBrand"
          />
        </div>
        <div className="flex flex-col items-center gap-1 text-center">
          <Typography variant="heading-3" className="text-contentPrimary">
            Get in touch with us
          </Typography>
          <Typography
            variant="paragraph-l-regular"
            className="max-w-sm text-center text-contentSecondary">
            You can contact us via LinkedIn, email (Gmail), or through our
            website using the links provided below.
          </Typography>
        </div>
      </div>
      <div className="rounded-xl max-w-full border p-5 bg-gray-100 border-b700 md:max-w-[70%]">
        <div className="flex flex-col gap-3 text-center">
          <Typography
            variant="paragraph-l-regular"
            className="text-contentPrimary">
            As described in the Project section, we welcome the submission of
            activity datasets against the viruses included in our database. To
            contribute your dataset, please email us with the relevant files and
            accompanying information, including your name, affiliation,
            institution, and a citation to the publication in which the data
            were originally reported. You may also include any additional
            comments or suggestions.
          </Typography>
          <Typography
            variant="paragraph-l-regular"
            className="text-contentPrimary">
            We look forward to new collaborations!
          </Typography>
        </div>
      </div>
      {/* Contact box */}
      <div className="w-full max-w-md rounded-2xl border border-borderPrimary bg-backgroundPrimary p-8 shadow-medium">
        <div className="flex flex-col gap-5">
          {/* Email row */}
          <div className="flex items-center gap-4 rounded-xl border border-borderPrimary bg-backgroundSecondary px-5 py-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-backgroundBrand/10">
              <DenovoIcon
                icon="email"
                width={20}
                height={20}
                className="text-contentBrand"
              />
            </div>
            <div className="flex flex-1 items-center justify-between gap-2">
              <Typography
                variant="paragraph-l-medium"
                className="text-contentPrimary">
                {CONTACT_EMAIL}
              </Typography>
              <Button
                variant="transparent"
                size="small"
                leftIcon={copied ? "check-bold" : "copy"}
                onPress={handleCopyEmail}
                aria-label="Copy email address"
              />
            </div>
          </div>
          {/* Website row */}
          <div className="flex items-center gap-4 rounded-xl border border-borderPrimary bg-backgroundSecondary px-5 py-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-backgroundBrand/10">
              <DenovoIcon
                icon="molecule"
                width={20}
                height={20}
                className="text-contentBrand"
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <a
                href="https://denovosciences.ai/"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer">
                <Typography
                  variant="paragraph-l-medium"
                  className="text-contentBrand underline-offset-2 hover:underline">
                  Denovo Sciences Website
                </Typography>
              </a>
            </div>
          </div>
          {/* LinkedIn row */}
          <div className="flex items-center gap-4 rounded-xl border border-borderPrimary bg-backgroundSecondary px-5 py-4">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-backgroundBrand/10">
              <DenovoIcon
                icon="linkedin"
                width={20}
                height={20}
                className="text-contentBrand"
              />
            </div>
            <div className="flex flex-col gap-0.5">
              <a
                href="https://am.linkedin.com/company/denovo-sciences"
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer">
                <Typography
                  variant="paragraph-l-medium"
                  className="text-contentBrand underline-offset-2 hover:underline">
                  Denovo Sciences LinkedIn
                </Typography>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
